import { Save, Gift } from "lucide-react";

const FIELDS = [["days", "Trial days"], ["active_projects", "Active projects"], ["ai_reviews", "AI reviews (total)"], ["users", "Users"], ["nudge_days_before_end", "Nudge N days before end"], ["reminder_day", "Reminder email on trial day"], ["plan_code", "Plan code"]];
const FLAGS = [["enabled", "Trials enabled"], ["self_serve", "Self-serve start on Billing page"], ["domain_check", "One trial per company email domain"], ["welcome_email", "Send welcome email with tips on trial start"]];
const inputCls = "bg-slate-950/60 border border-slate-800 rounded px-2 py-1 font-mono text-slate-100";
const when = (v) => (v ? new Date(v).toLocaleDateString() : "—");
const stateClass = (s) => {
  if (s === "converted") return "text-emerald-400";
  if (s === "active") return "text-sky-300";
  return "text-slate-400";
};

export const Stat = ({ label, value, testId }) => <div className="sc-card p-4" data-testid={testId}><div className="label-cap">{label}</div><div className="font-heading text-2xl font-extrabold text-slate-100 mt-1">{value}</div></div>;

export function TrialStats({ d }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
      <Stat label="Trials started" value={d.starts} testId="trial-stat-starts" />
      <Stat label="Active" value={d.active} testId="trial-stat-active" />
      <Stat label="Expired" value={d.expired} testId="trial-stat-expired" />
      <Stat label="Reviews exhausted" value={d.exhausted} testId="trial-stat-exhausted" />
      <Stat label="Converted" value={`${d.converted} · ${d.conversion_rate}%`} testId="trial-stat-converted" />
      <Stat label="Avg days to convert" value={d.avg_days_to_convert ?? "—"} testId="trial-stat-days" />
    </div>
  );
}

export function TrialRulesCard({ rules, setRule, saveSettings }) {
  return (
    <div className="sc-card p-5" data-testid="trial-rules-card">
      <div className="label-cap mb-2">Free-trial rules (stored in database)</div>
      {FIELDS.map(([k, l]) => (
        <label key={k} className="flex items-center justify-between gap-2 text-xs py-1"><span className="text-slate-400">{l}</span>
          <input value={rules[k] ?? ""} onChange={(e) => setRule(k, k === "plan_code" ? e.target.value : Number(e.target.value))} className={`w-28 ${inputCls}`} data-testid={`trial-rule-${k}`} /></label>
      ))}
      {FLAGS.map(([k, l]) => (
        <label key={k} className="flex items-center justify-between gap-2 text-xs py-1"><span className="text-slate-400">{l}</span><input type="checkbox" checked={!!rules[k]} onChange={(e) => setRule(k, e.target.checked)} data-testid={`trial-rule-${k}`} /></label>
      ))}
      <label className="block text-xs py-1"><span className="text-slate-400">Enabled review modes (comma-separated)</span>
        <input value={(rules.review_modes || []).join(", ")} onChange={(e) => setRule("review_modes", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={`mt-1 w-full ${inputCls}`} data-testid="trial-rule-review_modes" /></label>
      <button onClick={saveSettings} className="mt-2 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-1.5 rounded text-sm inline-flex items-center justify-center gap-1" data-testid="save-trial-rules-button"><Save className="w-3.5 h-3.5" /> Save trial rules</button>
    </div>
  );
}

export function TrialGrantCard({ grant, setGrant, grantable, doGrant }) {
  return (
    <div className="sc-card p-5 lg:col-span-2" data-testid="trial-grant-card">
      <div className="label-cap mb-2 flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> Grant a trial manually</div>
      <div className="flex gap-2">
        <select value={grant} onChange={(e) => setGrant(e.target.value)} className="flex-1 bg-slate-950/60 border border-slate-800 rounded px-2 py-2 text-sm text-slate-100" data-testid="trial-grant-select">
          <option value="">— choose a company without billing —</option>
          {grantable.map((c) => <option key={c.workspace_id} value={c.workspace_id}>{c.name}</option>)}
        </select>
        <button onClick={doGrant} disabled={!grant} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-4 rounded text-sm disabled:opacity-40" data-testid="trial-grant-button">Grant trial</button>
      </div>
      <div className="text-xs text-slate-500 mt-2">Uses the current rules. Bypasses self-serve eligibility, but a company can only ever have one trial.</div>
    </div>
  );
}

export function TrialRow({ t, resend }) {
  return (
    <tr data-testid={`trial-row-${t.workspace_id}`}>
      <td className="text-slate-100">{t.workspace_name}</td><td className="font-mono">{t.email}</td><td>{when(t.started_at)}</td><td>{when(t.ends_at)}</td>
      <td>{t.reviews_used}/{t.reviews_limit}</td><td>{t.projects_used}/{t.projects_limit}</td>
      <td className={`font-mono ${stateClass(t.state)}`}>{t.state}</td><td>{t.granted_by || "self-serve"}</td>
      <td>{when(t.welcome_sent_at)} <button onClick={() => resend(t, "welcome")} className="text-amber-400 underline ml-1" data-testid={`resend-welcome-${t.workspace_id}`}>send</button></td>
      <td>{when(t.reminder_sent_at)} <button onClick={() => resend(t, "reminder")} className="text-amber-400 underline ml-1" data-testid={`resend-reminder-${t.workspace_id}`}>send</button></td>
    </tr>
  );
}
